// https://ciwestlegacyartifacts.innovationwithin.services/
// westws.innovationwithin.services


import { HocuspocusProvider } from "@hocuspocus/provider";
import {
    Box,
    Stack
} from '@mui/material';
import { Decoration } from '@remirror/pm/view';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DecorationsExtension, Extension } from 'remirror';
import {
    AnnotationExtension, EntityReferenceExtension, EntityReferenceMetaData, EventsExtension, HardBreakExtension, ImageExtension, ItalicExtension,
    LinkExtension, TableExtension, UnderlineExtension, YjsExtension, findMinMaxRange, wysiwygPreset
} from 'remirror/extensions';
import * as Y from 'yjs';

import { DropCursorExtension } from 'remirror/extensions';


import { LinearProgress } from '@mui/material';
import {
    BasicFormattingButtonGroup,
    CreateTableButton,
    DataTransferButtonGroup,
    EditorComponent,
    HeadingLevelButtonGroup,
    HistoryButtonGroup,
    ListButtonGroup,
    OnChangeJSON,
    Remirror,
    TableComponents,
    VerticalDivider,
    useCurrentSelection,
    useHelpers,
    useRemirror,
    useRemirrorContext,
    useSelectedText,
} from "@remirror/react";

import { Theme, useTheme } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { debounce, noop } from 'lodash';
import { createPortal } from 'react-dom';
import { EDITOR_MAP, INSIGHT_ACTIONS, REMIRROR_BLOT_TYPES } from '../../constants';
import { artifactAtom } from '../../stores/jotai/artifacts/artifacts';
import { baseAtom, getArtifactByID, recieveFinding, selectInsight } from '../../stores/jotai/base';
import { discoverySessionAtom } from '../../stores/jotai/discovery-session';
import { findingsAtom } from '../../stores/jotai/insights/findings';
import { useUpdateFinding } from '../../tanstackQuery/queries/findings';
import { appLog } from '../../util';
//const currentTextSelectionColor = scssVars.mainButtonHighlight;
const defaultInsightColorForInlineHighlights = (theme: Theme)=> (theme.custom.disabledColor)
const colorFromID = (base,customFindingTypesData, findings, id, theme) => {
    let choosenColor = defaultInsightColorForInlineHighlights(theme);

    try {
        const finding = findings[id];
        let targetFindingType = undefined;

        for (let i = 0; i < finding?.joinery?.length; i++) {
            if (targetFindingType === undefined) {
                targetFindingType = (finding?.joinery[i]?.customFindingTypeID === "") ? undefined : finding?.joinery[i]?.customFindingTypeID;
            }
        }
        
        for (let i = 0; i < customFindingTypesData?.length; i++) {
            if (customFindingTypesData[i]?.customFindingTypeID === targetFindingType) {
                choosenColor = `${customFindingTypesData[i]?.color}`;
            }
        }
    }
    catch(e) {

    }

    if (choosenColor === undefined || choosenColor === null || choosenColor === "" || choosenColor.indexOf("#") === -1) {
        choosenColor = defaultInsightColorForInlineHighlights(theme);
    }

    return choosenColor;
}

function getCompliantTextColor(bgColor) {
    // Convert hex color to RGB
    const r = parseInt(bgColor?.slice(1, 3), 16);
    const g = parseInt(bgColor?.slice(3, 5), 16);
    const b = parseInt(bgColor?.slice(5, 7), 16);

    // Calculate brightness
    const brightness = (r * 0.299) + (g * 0.587) + (b * 0.114);

    // Return black (#000000) or white (#FFFFFF) text color based on brightness
    return brightness > 128 ? '#000000' : '#FFFFFF';
}

const decorateHighlights = (base: any, customFindingTypesData: any[], findings: any, highlights: EntityReferenceMetaData[][],theme:Theme): Decoration[] => {
    const decorations = highlights?.map((overlappingHighlights) => {
        const [from, to] = findMinMaxRange(overlappingHighlights);
        let firstSeenID = undefined;
        let bgColor = undefined;
        let style = "";

        for (let i = 0; i < overlappingHighlights?.length; i++) {
            firstSeenID = overlappingHighlights[i]?.id;
        }

        if (!!firstSeenID) {
            bgColor = colorFromID(base, customFindingTypesData, findings, firstSeenID, theme);
            style = `
                background-color: ${bgColor} !important;
                color: ${getCompliantTextColor(bgColor)};
                padding: 2px;
                margin: 0 -3px 0 -1px;
                border-radius: 4px;
            `;
        }

        // Add decoration to all inline nodes in the given range.
        return Decoration.inline(from, to, { style });
    });

    return [...decorations];
};

function stringToHslColor(str, s, l) {
    if (typeof str !== 'string' || !str.length) {
        return `hsl(0, ${s}%, ${l}%)`;
    }

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const h = ((hash % 360) + 360) % 360;
    return `hsl(${h}, ${s}%, ${l}%)`;
}

const websockets = {};
let currentArtifactConnectionKey = null;

function safelyDestroySocket(artifactID) {
    const socket = websockets[artifactID];
    if (socket) {
        try {
            socket.awareness?.setLocalState(null);
            socket.disconnect();
            socket.destroy();
        } catch (e) {
            console.warn("Error destroying socket for", artifactID, e);
        }
        delete websockets[artifactID];
    }
}

const connectWebsocket = ({
    artifactID,
    templateID,
    artifactType,
    artifactUrl,
    userID,
    username,
    onSynced,
}) => {
    const color = stringToHslColor(username, 80, 30);
    const ydoc = new Y.Doc();

    if (currentArtifactConnectionKey && currentArtifactConnectionKey !== artifactID) {
        safelyDestroySocket(currentArtifactConnectionKey);
    }

    const existingSocketConnection = websockets?.[artifactID];
    if (existingSocketConnection) {
        if (!existingSocketConnection.isConnected) {
            existingSocketConnection.connect();
        }
        return existingSocketConnection;
    }

    appLog(`websocket provider setting up for artifact ${artifactID} with user ${userID}`);

    const wsProvider = process.env.NEXT_PUBLIC_REALTIME_SERVER_WEBSOCKET_URL;
    const newSocketConnection = new HocuspocusProvider({
        url: wsProvider,
        name: artifactID,
        document: ydoc,
        parameters: {
            templateID,
            artifactID,
            artifactType,
            artifactUrl,
            userID,
        },
        onDestroy: () => {
            appLog(`onDestroy triggered for artifact ${artifactID}`);
            delete websockets[artifactID];
        },
        onSynced: data => {
            onSynced(data);
        },
    });

    newSocketConnection.awareness?.setLocalStateField("user", {
        color,
        name: username,
    });

    newSocketConnection.__username = username;
    websockets[artifactID] = newSocketConnection;
    currentArtifactConnectionKey = artifactID;

    return newSocketConnection;
};

const getExtensions = ({
    base,
    findings,
    customFindingTypesData,
    websocketProvider,
    theme
}) => {
    let entityExtension = new EntityReferenceExtension({
        //@ts-ignore
        getStyle: (highlights: EntityReferenceMetaData[][]) => {
            return decorateHighlights(base,customFindingTypesData,findings, highlights, theme);
        },
    });

    let eventsExtension = new EventsExtension({});

    const handleMouseUp = useCallback((event) => {
        return false;
    }, []);

    eventsExtension.addHandler('mouseup', handleMouseUp);

    const extensions: Extension[] = [
        new ItalicExtension(),
        new UnderlineExtension(),
        new DropCursorExtension(),
        new ImageExtension(),
        new TableExtension(),
        new HardBreakExtension(),
        eventsExtension,
        new LinkExtension({ autoLink: true }),
        entityExtension,
        new AnnotationExtension(),
        new DecorationsExtension({

        }),
        new EventsExtension(),
        ...wysiwygPreset(),
    ]

    if (!!websocketProvider) {
        extensions.push(
            new YjsExtension({
                getProvider: () => {
                    return {
                        doc: websocketProvider?.document,
                        awareness: websocketProvider?.awareness,
                        destroy: () => {},
                        disconnect: () => {},
                    };
                },
                disableUndo: true,
            })
        );
    }

    return extensions;
}

const handleResponse = (response, setFindings, selectedFinding, setBase) => {
    if (response?.findingID) {
        // Update findings state
        setFindings((prevFindings) => ({
            ...prevFindings,
            [response.findingID]: {
                ...response,
            },
        }));

        // Update selectedFinding in base state if IDs match
        if (selectedFinding?.findingID === response?.findingID) {
            setBase((prevBase) => ({
                ...prevBase,
                selectedFinding: response,
            }));
        }
    }
};

const getCustomFindingTypeIDAsString = (insight, customFindingTypesData, base) => {
    if (insight?.joinery?.length > 0) {
        return insight?.joinery.map((type) => type?.customFindingTypeID).join(",");
    } else {
        const findingTypeID = insight?.findingTypeID;
        const findingTypes = base?.findingTypes || [];
        const customFindingTypes = customFindingTypesData || [];

        const findingTypeName = findingTypes?.find(
            (findingType) => findingType?.findingTypeID === findingTypeID
        )?.name;

        const customFindingTypeID = customFindingTypes?.find(
            (customFindingType) =>
                customFindingType?.label === findingTypeName
        )?.customFindingTypeID;
        
        return customFindingTypeID;
    }
};

const MissingHighlightMonitor = (props) => {
    const { artifactID, isLoadingDocument } = props;
    const [findings, setFindings] = useAtom(findingsAtom);

    const hasMissingHighlight = (finding) => {
        let quote: any = {};
        try {
            quote = JSON.parse(finding?.quote);
        } catch {
            return false;
        }
        return (
            finding?.artifactID === artifactID &&
            finding?.isDraft === null &&
            quote?.source === "note" &&
            quote?.selection?.anchor &&
            quote?.selection?.head
        );
    };

    const getFindingIDsWithMissingHighlights = () => {
        return Object.values(findings)
            ?.filter((finding) => hasMissingHighlight(finding))
            .map((finding) => finding?.findingID)
            .join(",");
    };

    const publishMessageToWindowForCreatingHighlights = () => {
        const findingIDs = getFindingIDsWithMissingHighlights();
    
        if (findingIDs) {
            window.postMessage(
                `remirrorFormat~~${REMIRROR_BLOT_TYPES.INSIGHT}~~${INSIGHT_ACTIONS.BULK_CREATE}~~${findingIDs}`
            );
        }
    };

    useEffect(() => {
        if (!isLoadingDocument) {
            publishMessageToWindowForCreatingHighlights();
        }
    }, [findings, isLoadingDocument]);

    return <span />;
}

const HighlightedTextChangeMonitor = (props) => {
    const { getEntityReferences } = useHelpers<EntityReferenceExtension>(true);
    const [base, setBase] = useAtom(baseAtom);
    const [findings, setFindings] = useAtom(findingsAtom);
    const { keycloak } = useKeycloak();
    const discoverySession = useAtomValue(discoverySessionAtom);
    const client = useQueryClient()
    const { mutateAsync: updateFinding } = useUpdateFinding({ accessToken: keycloak?.token }, {onSuccess: () => {
        client.invalidateQueries({ queryKey: ["insights"] });
    }})

    
    // Re-render editor to reflect highlight color
    const { view } = useRemirrorContext();
    const newState = view.state.apply(view.state.tr);
    view.updateState(newState);

    const updateInsights = useCallback(
        debounce(() => {
            const allHighlights = getEntityReferences();
            allHighlights.forEach((highlight) => {
                const { id, text, from, to } = highlight;
                const changedInsight = findings[id]
                
                if (!!changedInsight) {
                    const previousQuote = JSON.parse(changedInsight.quote);
                    const newHighlightedQuote = {
                        selection: {
                            anchor: from,
                            head: to,
                            type: "text",
                        },
                        source: "note",
                        textCache: text,
                    };

                    if (
                        previousQuote?.selection?.anchor !== from ||
                        previousQuote?.selection?.head !== to ||
                        previousQuote?.textCache !== text
                    ) {
                        const insightDetails = {
                            editor: EDITOR_MAP.MATERIAL,
                            artifactID: changedInsight?.artifactID,
                            findingID: changedInsight?.findingID,
                            comment: changedInsight?.comment,
                            created: changedInsight?.created,
                            hypothesisLinks:JSON.stringify(changedInsight?.hypothesisLinks),
                            typeID: getCustomFindingTypeIDAsString(changedInsight, props?.customFindingTypes, base),
                            quote: newHighlightedQuote,
                            interviewID: changedInsight?.interviewID,
                            rating: changedInsight?.rating?.global?.rating || 0,
                            sessionID:discoverySession?.sessionID
                        };

                        updateFinding(insightDetails).then((response) => {
                            handleResponse (response, setFindings, base?.selectedFinding, setBase);
                        });
                    }
                }
            });
        }, 500),
        [findings]
    );

    updateInsights();

    return <span />;
}

const SelectionMonitor = (props) => {
    const text = useSelectedText();
    const selection = useCurrentSelection();
    const [base, setBase] = useAtom(baseAtom);
    const [textCache, setTextCache] = useState("");

    useEffect(() => {
        if (!text) {
            //@ts-ignore
            window.__lastInsightSelection = undefined;
        }
        else {
            const result = {
                selection: selection,
                textCache: text,
                source: 'note'
            };

            setTextCache(text);

            if (!!base.selectedFinding) {
                if (textCache !== text) {
                    selectInsight(setBase, undefined);
                }
            }

            //@ts-ignore
            window.__lastInsightSelection = window?.__lastInsightSelection !== null ? result : undefined;
            // window.__lastInsightSelection =  result;

            props.onSet(result);
        }
    }, [text, selection, textCache]);
    return <span />
}

function initializeWindowTimestampCache() {
    try {
        if (window.__artifactTimeStampCache === undefined) {
            window.__artifactTimeStampCache = {}
        }
    }
    catch (e) {

    }
}

function storeArtifactEditTimestamp(artifactID) {
    try {
        const now = new Date()
        const utcMilllisecondsSinceEpoch = now.getTime() + (now.getTimezoneOffset() * 60 * 1000)
        const utcSecondsSinceEpoch = utcMilllisecondsSinceEpoch;

        window.__artifactTimeStampCache[artifactID] = utcSecondsSinceEpoch;
    }
    catch (e) {

    }
}

function MaterialArtifactEditorInner(props) {
    const {
        templateID,
        artifactType,
        artifactID,
        artifact,
        userID,
        username,
        realtime,
        useToolBar,
        disableDefaultBorder=false
    } = props;
    const [initializedState, setInitializedState] = useState(false);
    const [lastLocalSelection, setLastLocalSelection] = useState(undefined);
    const [base, setBase] = useAtom(baseAtom);
    const [findings,setFindings]=useAtom(findingsAtom);
    const [isLoadingDocument, setIsLoadingDocument] = useState(true);
    const { keycloak, initialized } = useKeycloak();
    const discoverySession = useAtomValue(discoverySessionAtom);
    const theme = useTheme();
    const client = useQueryClient()
    const customFindingTypesData = client.getQueryData(["custom-finding-types", { groupID: base?.team?.groupID }]);

    const hocuspocusWebsocketProvider = realtime
        ? connectWebsocket({
                artifactID,
                templateID,
                artifactType,
                artifactUrl: artifact?.artifact?.dataURL,
                userID,
                username,
                onSynced: (isDocumentSynced) => {
                    setIsLoadingDocument(!isDocumentSynced);
                },
            })
        : null;

    appLog('mounting internal artifact editor');

    const { manager, state, getContext } =
        useRemirror({
            extensions: getExtensions({
                base,
                findings,
                customFindingTypesData,
                websocketProvider: hocuspocusWebsocketProvider,
                theme
            }),
            builtin: { persistentSelectionClass: "selection" },
            content: props?.initialData,
            selection: "start",
            stringHandler: "html",
        });

    const eventsExtension = manager.getExtension(EventsExtension);

    const { mutateAsync: updateFinding } = useUpdateFinding({ accessToken: keycloak?.token }, {onSuccess: () => {
        client.invalidateQueries({ queryKey: ["insights"] })
    
        // set the jotai base state
        }})


    //Scroll to text implementation
    const context = getContext();
    useEffect(() => {
        if (context && context.commands) {
            const quoteFromText = base?.selectedFinding?.quote;
            const rangeData = quoteFromText && JSON?.parse(quoteFromText);
    
            if (rangeData?.selection?.anchor) {
                const domAtPosition = context.view.domAtPos(rangeData?.selection?.anchor);
                const targetNode = domAtPosition?.node;
                
                if (targetNode) {
                    const scrollTarget = (targetNode.nodeType === Node.TEXT_NODE)
                        ? targetNode?.parentElement 
                        : targetNode;
    
                    if (scrollTarget && scrollTarget.scrollIntoView) {
                        scrollTarget.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                    } else {
                        // console.warn("No valid scroll target found:", scrollTarget);
                    }
                }
            }
        }
    }, [base.selectedFinding, context]);

    const removeEntryReferenceFromSelectedData = () => {
        const selection = document?.getSelection();
        const range = selection?.getRangeAt(0).cloneRange();
        const tempDiv = document?.createElement("div");

        // Extract the selected content as a DocumentFragment and append it to the temporary div
        tempDiv.appendChild(range.cloneContents());

        // Get the span elements with data-entity-reference attribute
        const spanElements = tempDiv.querySelectorAll(
            "span[data-entity-reference]"
        );

        // Replace each span element with its inner HTML content
        spanElements?.forEach((span) => {
            const content = span.innerHTML;
            span.outerHTML = content;
        });

        return tempDiv.innerHTML;
    }

    // Add event handler for paste event
    eventsExtension.addHandler('copy', (event) => {
        event.preventDefault();

        // Get the selection
        const filteredData = removeEntryReferenceFromSelectedData();

        // Set the HTML content to clipboard
        event.clipboardData.setData("text/html", filteredData);
    });

    useEffect(() => {
        if (state !== undefined) {
            const context = getContext();

            if (context !== undefined && context.commands !== undefined) {
                let hasAnnoationAlready = false;

                try {
                    //@ts-ignore
                    const existingSet = state.annotation$.annotations;

                    for (let i = 0; i < existingSet?.length; i++) {
                        if (existingSet[i]?.id === 'current-selected-word') {
                            hasAnnoationAlready = true;
                        }
                    }
                }
                catch(e) {

                }

                if (hasAnnoationAlready) {
                    context.commands.updateAnnotation('current-selected-word', {
                        from: lastLocalSelection?.selection?.anchor,
                        to: lastLocalSelection?.selection?.head,
                    })
                }
                else {
                    context.commands.addAnnotation({
                        from: lastLocalSelection?.selection?.anchor,
                        to: lastLocalSelection?.selection?.head,
                        id: 'current-selected-word'
                    })
                }
            }
        }

    }, [lastLocalSelection])

    const saveFindingToLocalState = (finding) => {
        recieveFinding(setBase, finding, false);
    }

    const pushMissingHighlights = (contextCommands, quote, id) => {
        contextCommands.selectText({
            from: quote?.selection?.anchor,
            to: quote?.selection?.head,
        });
        contextCommands.addEntityReference(id);
    }

    const saveInsightToDatabase = (insightDetails) => {
        updateFinding(insightDetails).then((response) => {
            handleResponse (response, setFindings, base?.selectedFinding, setBase)
        });
    };

    const bulkGenerateHighlights = (blotEntityID, contextCommands) => {
        blotEntityID.split(",").forEach((id) => {
            const finding = findings[id];
            const quote = JSON.parse(finding?.quote);
            const insightDetails = {
                editor: EDITOR_MAP.MATERIAL,
                artifactID: finding?.artifactID,
                findingID: finding?.findingID,
                comment: finding?.comment,
                created: finding?.created,
                hypothesisLinks: JSON.stringify(
                    finding?.hypothesisLinks
                ),
                typeID: getCustomFindingTypeIDAsString(
                    finding,
                    customFindingTypesData,
                    base
                ),
                quote: JSON.parse(finding?.quote),
                interviewID: finding?.interviewID,
                rating: finding?.rating?.global?.rating || 0,
                isDraft: 0,
                sessionID:discoverySession?.sessionID
            };

            //Have to look into this
            saveFindingToLocalState(finding);
            pushMissingHighlights(contextCommands, quote, id);
            saveInsightToDatabase(insightDetails); 
        });
    }

    const handleRemirrorFormat = (e) => {
        const data = e?.data;
        const contextForFormat = getContext();

        if (
            typeof data === "string" &&
            !!contextForFormat &&
            data.includes("~~")
        ) {
            const [_, blotType, blotAction, blotEntityID] = data.split("~~");
            const contextCommands = contextForFormat.commands;

            if (blotType === REMIRROR_BLOT_TYPES.INSIGHT) {
                if (blotAction === INSIGHT_ACTIONS.CREATE) {
                    contextCommands.addEntityReference(blotEntityID);
                } else if (blotAction === INSIGHT_ACTIONS.DELETE) {
                    contextCommands.removeEntityReference(blotEntityID);
                }
                else if(blotAction === INSIGHT_ACTIONS.BULK_CREATE) {
                    bulkGenerateHighlights(blotEntityID, contextCommands)
                }
            } else {
                try {
                    contextCommands["toggle" + blotType]();
                } catch (e2) {
                    //console.log(e2);
                }
            }
        }
    };

    useEffect(() => {
        if (
            !!hocuspocusWebsocketProvider &&
            hocuspocusWebsocketProvider?.isSynced
        ) {
            setIsLoadingDocument(false);
        }
    }, [])

    useEffect(() => {
        if (!realtime) {
            setIsLoadingDocument(false);
        }
    }, [realtime])

    React.useEffect(() => {
        window.addEventListener('message', handleRemirrorFormat);

        // cleanup this component
        return () => {
            window.removeEventListener('message', handleRemirrorFormat);
        };
    }, []);

    useEffect(() => {
        initializeWindowTimestampCache();

        try {
            setInitializedState(true);
        }
        catch (e) {

        }

        try {
            try {
                window.__lastKnownEditorKey = props?.artifactID;
            }
            catch (e) {

            }
        }
        catch (e) {

        }
    }, [])

    const [editable, setIsEditable] = useState(props.isReadOnly === true ? false : true);

    const markHumanInteraction = () => {
        storeArtifactEditTimestamp(props?.artifactID);
    }

    const renderToolbar = () => {
        const hasPortal = (props?.portal !== undefined);

        let rootToolbar = (
            <Box
                sx={{
                    display: "flex",
                    gap: "4px",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    alignContent: "center",
                    "> .MuiBox-root": {
                        gap: "4px",
                        background: "transparent",
                        ".MuiButtonBase-root": {
                            background: theme?.custom?.white,
                        }
                    },
                }}
            >
                <HistoryButtonGroup />
                <VerticalDivider />
                <DataTransferButtonGroup />
                <VerticalDivider />
                <HeadingLevelButtonGroup />
                <VerticalDivider />
                <BasicFormattingButtonGroup />
                <VerticalDivider />
                <ListButtonGroup>
                    <CreateTableButton />
                </ListButtonGroup>
            </Box>
        );

        if (props.isReadOnly === true) {
            rootToolbar = <div></div>
        }

        if (hasPortal) {
            return createPortal(rootToolbar, props.portal());
        }
        else {
            return rootToolbar
        }
    }

    return (
        <Box
            onKeyDown={(e) => {
                if (editable) {
                    markHumanInteraction();
                } else {
                    e.preventDefault();
                }
            }}
            onDrop={() => {
                if (editable) {
                    markHumanInteraction();
                }
            }}
            onPaste={() => {
                if (editable) {
                    markHumanInteraction();
                }
            }}
            className="remirror-theme"
            sx={{
                height: "calc(100% + 57px)",
                ".remirror-editor.ProseMirror .ProseMirror-yjs-cursor > div": {
                    padding: "4px 8px",
                    borderRadius: "4px",
                    left: "-2px",
                    top: "-10px",
                    textTransform: "capitalize",
                },
                ".remirror-editor.ProseMirror pre": {
                    whiteSpace: "normal",
                },
            }}
        >
            <Remirror
                onChange={function noRefCheck() {}}
                manager={manager}
            >
                <HighlightedTextChangeMonitor customFindingTypes={customFindingTypesData} />
                <SelectionMonitor
                    onSet={(e) => {
                        if (editable) {
                            setLastLocalSelection(e);
                        }
                    }}
                />
                <div
                    style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                       
                    }}
                >
                    {props?.useToolBar ? renderToolbar() : null}
                    <Stack
                        sx={{
                            height: "100%",
                            overflowX:'scroll',
                            margin: "12px 0 0",
                            position: "relative",
                            borderRadius: "4px",
                            borderTop: `1px solid ${theme?.custom?.borderGrey}`,
                            ".remirror-editor-wrapper": {
                                paddingTop: "0 !important",
                                borderRadius:'4px',
                            },
                            "&:focus-within .remirror-editor-wrapper": {
                                border: `1px solid ${theme?.custom?.mainButtonHighlight}`,
                            },
                            "&:focus-within": {
                                borderTop: `1px solid ${theme?.custom?.mainButtonHighlight}`, 
                            },
                            ...(disableDefaultBorder && {
                               "& .remirror-editor.ProseMirror": {
                                border: "none !important",
                                borderBottom: "none !important",
                                boxShadow: "none !important",
                                outline: "none !important",
                              }
                            }),
                        }}
                    >
                        {isLoadingDocument && (
                             <LinearProgress />
                        )}
                        <EditorComponent />                        
                        <MissingHighlightMonitor artifactID={artifactID} isLoadingDocument={isLoadingDocument}/>
                    </Stack>
                </div>
                <TableComponents />
                <OnChangeJSON
                    onChange={(newValue) => {
                        if (editable) {
                            props.onChange(newValue);
                        }
                    }}
                />
            </Remirror>
        </Box>
    );
}

/*
next-dev.js:20 
        
       Warning: Cannot update a component (`AuthorshipMonitor`) while rendering a different component (`Remirror`). To locate the bad setState() call inside `Remirror`, follow the stack trace as described in https://reactjs.org/link/setstate-in-render
    at Remirror (webpack-internal:///./node_modules/@remirror/react-core/dist/remirror-react-core.js:607:5)
    at div
    at div*/

interface IMaterialArtifactManagerProps {
    onChange?: Function;
    onLastEdit?: Function;
    config: any;
    isReadOnly?: boolean;
    portal: any;
    username: string;
    userID: string;
    artifactType: string;
    templateID?: string;
    useToolBar?: boolean;
    disableDefaultBorder?:boolean;
}

const MaterialArtifactManager: React.FC<IMaterialArtifactManagerProps> = (props) => {
    const { onChange, templateID, artifactType, disableDefaultBorder=false } = props;
    const [wrapperSelectionCache, setWrapperSelectionCache] = useState(undefined);
 

    const currentConfig = useCallback(() => {
        return {
            onChange: (e) => {
                onChange(e);

                if (props.onLastEdit) {
                    props.onLastEdit();
                }
            },
            onSelectionSet: (selection) => {

            },
            ...props.config
        }
    }, [props.config]);

    let isReadOnly = props.isReadOnly ? props.isReadOnly : false;

    let momizedEditor = useMemo(() => {
        const config = currentConfig();

        return (
            <MaterialArtifactEditorInner
                portal={props?.portal}
                isReadOnly={isReadOnly}
                realtime={true}
                username={props?.username}
                userID={props?.userID}
                artifactID={config.artifactID}
                onSelectionSet={config.onSelectionSet}
                onChange={config.onChange}
                initialData={config.initialData}
                artifact={config.artifact}
                artifactType={artifactType}
                templateID={templateID}
                useToolBar={props?.useToolBar}
                disableDefaultBorder={disableDefaultBorder}
            />
        );
    }, [currentConfig, isReadOnly]);

    return <div style={{ height: "100%" }}>
        {momizedEditor}
    </div>
}

MaterialArtifactManager.defaultProps = {
    onChange: noop,
    onLastEdit: noop,
};

interface IMaterialArtifactEditorProps {
    artifactID: string;
    artifactType: string;
    templateID?: string;
	onChange?: Function;
	portal?: any;
	isReadOnly?: boolean;
    useToolBar?: boolean;
    disableDefaultBorder?: boolean;
}

const MaterialArtifactEditor: React.FC<IMaterialArtifactEditorProps> = (props) => {
    const { onChange, templateID, artifactType, disableDefaultBorder=false } = props;
    const base = useAtomValue(baseAtom);
    const [localArtifact, setLocalArtifact] = useState(undefined);
    const { keycloak } = useKeycloak();
    const [loadingInitialDataState, setLoadingInitialDataState] = useState(false);
    const [lastEditTime, setLastEditTime] = useState(undefined);
    const [artifacts,]=useAtom(artifactAtom);

    const artifact = getArtifactByID(props?.artifactID, artifacts);

    let token = undefined;

    try {
        token = keycloak.token;
    }
    catch (e) {

    }

    let sid = undefined;

    try {
        sid = window.__sid;
    }
    catch (e) {

    }

    const currentConfig = useCallback(() => {
        let _artifactID = props?.artifactID ? props?.artifactID.toString() : undefined;

        return {
            artifact,
            artifactID: _artifactID,
            userID: base?.user?.userID
        }
    }, [artifact, props?.artifactID]);

    // fetch our initial data
    //@ts-ignore
    useEffect(() => {
        const config = currentConfig();

        let loadingInitialData = true;
        setLoadingInitialDataState(true);

        // declare the async data fetching function
        const fetchInitialData = async () => {
            if (config.artifactID !== undefined) {
                let artifactID = undefined;
                let artifact = config?.artifact;
                let dataURL = undefined;

                if (artifact === undefined || artifact === null) {
                    dataURL = `${process.env.NEXT_PUBLIC_LINX_URL}/state/dynamicartifact?artifactID=` + config.artifactID + "&groupID=" + base.team.groupID;
                    artifactID = config?.artifactID;
                }
                else {
                    dataURL = artifact.dataURL;

                    // check for a legacy data URL
                    if (dataURL.includes("legacy-")) {
                        dataURL = dataURL.replace("legacy-", "https://8ajs9gsn5g.execute-api.us-west-2.amazonaws.com/ci/artifact?objectID=");
                    }

                    artifactID = artifact?.artifactID;
                }

                if (loadingInitialData) {
                    setLocalArtifact({
                        artifact: config,
                        initialData: "",
                        artifactID: artifactID
                    })
                }
            }
        }

        // when we re-memoize an editor clear the insight
        try {
            //window.__lastInsightSelection = undefined;
        }
        catch (e) {

        }

        fetchInitialData()
            .catch(console.error);

        // cancel any future `setData`
        return () => {
            loadingInitialData = false;
            // when we re-memoize an editor clear the insight
            try {
                //window.__lastInsightSelection = undefined;
            }
            catch (e) {

            }
        }
    }, [currentConfig]);

    let knownEditorKey = 'none';

    try {
        const subArtifactID = localArtifact?.artifact?.artifactID;

        if (subArtifactID === props?.artifactID) {
            knownEditorKey = subArtifactID;
        }
    }
    catch (e) {

    }

    let innerContents = <LinearProgress />

    if (localArtifact !== undefined && localArtifact.artifact !== undefined) {
        innerContents = (
            <MaterialArtifactManager
                onLastEdit={() => {
                    setLastEditTime(new Date().toUTCString());
                }}
                onChange={onChange}
                isReadOnly={props?.isReadOnly}
                portal={props?.portal}
                username={base?.user?.username}
                userID={base?.user?.userID}
                key={knownEditorKey + "-manager"}
                config={localArtifact}
                templateID={templateID}
                artifactType={artifactType}
                useToolBar={props?.useToolBar}
                disableDefaultBorder={disableDefaultBorder}
            />
        );
    }

    return <div style={{ height: "100%" }} id={knownEditorKey + "-parent"} key={knownEditorKey + "-parent"}>
        {innerContents}
    </div>
}

MaterialArtifactEditor.defaultProps = {
    onChange: noop,
};

export default MaterialArtifactEditor;
